import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import apiCall from 'auth/ApiCallInstance';
import { AUTH_TOKEN } from 'constants/AuthConstant';

const PermissionContext = createContext(null);

// Resource to route key mapping
const RESOURCE_ROUTE_MAP = {
  dashboard: ['express-nextgen-dashboard'],
  users: ['express-nextgen-users'],
  bookings: ['express-nextgen-bookings', 'express-nextgen-calender'],
  games: ['express-nextgen-games'],
  settings: ['express-nextgen-settings'],
  reports: ['express-nextgen-reports', 'express-nextgen-audit-logs'],
  transactions: ['express-nextgen-transactions'],
  gift_cards: ['express-nextgen-gift-cards'],
  promos: ['express-nextgen-promo'],
  backups: ['express-nextgen-backups'],
  customers: ['express-nextgen-customers'],
  blocked_slots: ['express-nextgen-blocked-slots'],
  holidays: ['express-nextgen-holidays'],
  waivers: ['express-nextgen-waivers'],
  notifications: ['express-nextgen-notifications'],
};

export const PermissionProvider = ({ children }) => {
  // Get token from Redux to trigger re-fetch when auth state changes
  const reduxToken = useSelector(state => state.auth.token);
  
  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState({});
  const [rolesConfig, setRolesConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mustChangePassword, setMustChangePassword] = useState(false);

  // Fetch current user and their permissions
  const fetchUserData = useCallback(async () => {
    const token = localStorage.getItem(AUTH_TOKEN);
    if (!token) {
      setUser(null);
      setPermissions({});
      setRolesConfig(null);
      setMustChangePassword(false);
      setLoading(false);
      return;
    }

    try {
      const [userResponse, configResponse] = await Promise.all([
        apiCall.get('/auth/me'),
        apiCall.get('/users/roles-permissions'),
      ]);

      const userData = userResponse.data.user;
      const configData = configResponse.data;
      setUser(userData);
      setRolesConfig(configData);
      setMustChangePassword(userData?.mustChangePassword || false);

      // Calculate effective permissions
      const effectivePerms = {};
      if (userData) {
        const { role, useCustomPermissions, customPermissions } = userData;
        
        // Get default permissions for role
        const defaultPerms = configData.defaultRolePermissions?.[role] || {};
        
        // Use custom permissions if enabled AND customPermissions has content, otherwise use default
        let permsToUse = defaultPerms;
        if (useCustomPermissions && customPermissions) {
          const customPermsObj = customPermissions instanceof Map 
            ? Object.fromEntries(customPermissions) 
            : customPermissions;
          
          // Only use custom permissions if they actually have content
          if (Object.keys(customPermsObj).length > 0) {
            permsToUse = customPermsObj;
          }
        }

        // Super admin gets all permissions
        if (role === 'super_admin') {
          configData.resources?.forEach(resource => {
            effectivePerms[resource] = ['read', 'create', 'update', 'delete'];
          });
        } else {
          Object.assign(effectivePerms, permsToUse);
        }
      }
      
      console.log('[Permissions] Effective permissions:', effectivePerms);
      setPermissions(effectivePerms);
    } catch (error) {
      console.error('Failed to fetch user data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Re-fetch when Redux token changes (login/logout)
  useEffect(() => {
    fetchUserData();
  }, [fetchUserData, reduxToken]);

  // Check if user has specific permission
  const hasPermission = useCallback((resource, action) => {
    if (!user) return false;
    
    // Super admin always has access
    if (user.role === 'super_admin') return true;
    
    const resourcePerms = permissions[resource];
    
    // Check if resourcePerms exists and is a non-empty array
    if (!resourcePerms || !Array.isArray(resourcePerms) || resourcePerms.length === 0) {
      return false;
    }
    
    return resourcePerms.includes(action);
  }, [user, permissions]);

  // Check if user can access a navigation route
  const canAccessRoute = useCallback((routeKey) => {
    // Dashboard is always accessible if user has a token (even if user data not loaded)
    if (routeKey === 'express-nextgen-dashboard') return true;
    
    // If user data not loaded yet, only allow dashboard
    if (!user) return false;
    
    // Super admin can access everything
    if (user.role === 'super_admin') return true;
    
    // Find the resource for this route
    for (const [resource, routeKeys] of Object.entries(RESOURCE_ROUTE_MAP)) {
      if (routeKeys.includes(routeKey)) {
        // User needs at least 'read' permission on the resource
        return hasPermission(resource, 'read');
      }
    }
    
    // If no mapping found, allow access (backward compatibility)
    return true;
  }, [user, hasPermission]);

  // Filter navigation items based on permissions
  const filterNavigation = useCallback((navItems) => {
    // Always show at least the navigation structure (with Dashboard accessible)
    // This prevents empty nav while loading user data
    return navItems.map(group => {
      if (!group.submenu || group.submenu.length === 0) {
        return canAccessRoute(group.key) ? group : null;
      }
      
      const filteredSubmenu = group.submenu.filter(item => canAccessRoute(item.key));
      
      if (filteredSubmenu.length === 0) return null;
      
      return { ...group, submenu: filteredSubmenu };
    }).filter(Boolean);
  }, [canAccessRoute]);

  // Refresh user data (call after login or permission changes)
  const refreshUser = useCallback(() => {
    return fetchUserData();
  }, [fetchUserData]);

  const value = {
    user,
    permissions,
    rolesConfig,
    loading,
    mustChangePassword,
    hasPermission,
    canAccessRoute,
    filterNavigation,
    refreshUser,
  };

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
};

export const usePermissions = () => {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error('usePermissions must be used within a PermissionProvider');
  }
  return context;
};

export default PermissionContext;
